import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { Menu, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { supabase } from "@/utils/supabase";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/pricing", label: "Pricing" },
  { to: "/docs", label: "Docs" },
  { to: "/blog", label: "Blog" },
] as const;

const MOBILE_ONLY_LINKS = [
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
  { to: "/privacy", label: "Privacy" },
  { to: "/terms", label: "Terms" },
] as const;

export function PublicNavbar() {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  const closeMenu = () => setMobileMenuOpen(false);

  return (
    <header className="sticky top-0 z-20 border-b border-border bg-background/80 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
        <Link
          to="/"
          className="flex items-center gap-2.5 text-base font-semibold text-primary"
        >
          <img
            src="/todollm-icon.png"
            alt="TodoLLM"
            className="h-7 w-7 rounded-lg"
          />
          TodoLLM
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-6 text-sm md:flex">
          {NAV_LINKS.map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              className="text-muted-foreground transition-colors hover:text-foreground"
            >
              {label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {/* Desktop auth */}
          <div className="hidden items-center gap-2 md:flex">
            {session === undefined ? null : session ? (
              <Button asChild size="sm">
                <Link to="/dashboard">Go to Dashboard</Link>
              </Button>
            ) : (
              <>
                <Button asChild variant="ghost" size="sm">
                  <Link to="/log-in">Log in</Link>
                </Button>
                <Button asChild size="sm">
                  <Link to="/sign-up">Sign up</Link>
                </Button>
              </>
            )}
          </div>

          {/* Mobile menu toggle */}
          <button
            className="rounded-md p-1 text-muted-foreground hover:text-foreground md:hidden"
            onClick={() => setMobileMenuOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="border-t border-border bg-background/95 px-6 pb-4 pt-3 backdrop-blur md:hidden">
          <nav className="flex flex-col gap-0.5 text-sm">
            {NAV_LINKS.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                onClick={closeMenu}
                className="rounded-md px-2 py-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                {label}
              </Link>
            ))}
            {MOBILE_ONLY_LINKS.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                onClick={closeMenu}
                className="rounded-md px-2 py-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                {label}
              </Link>
            ))}
            <a
              href="https://stats.uptimerobot.com/m0exbqGCmL"
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeMenu}
              className="rounded-md px-2 py-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              Status
            </a>
          </nav>
          <div className="mt-3 flex gap-2 border-t border-border pt-3">
            {session === undefined ? null : session ? (
              <Button asChild size="sm" className="w-full">
                <Link to="/dashboard" onClick={closeMenu}>
                  Go to Dashboard
                </Link>
              </Button>
            ) : (
              <>
                <Button asChild variant="outline" size="sm" className="flex-1">
                  <Link to="/log-in" onClick={closeMenu}>
                    Log in
                  </Link>
                </Button>
                <Button asChild size="sm" className="flex-1">
                  <Link to="/sign-up" onClick={closeMenu}>
                    Sign up
                  </Link>
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
